import { useEffect, useState } from 'react';

const KEY = 'theme';

const systemPrefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches;

const valid = (v) => (v === 'light' || v === 'dark' ? v : null);

const stored = () => {
  try {
    return valid(localStorage.getItem(KEY));
  } catch {
    return null;
  }
};

/* The inline script in index.html has already resolved and applied a theme
   before first paint. Honour it rather than recomputing, so mounting never
   overrides a decision the page has already rendered with. */
const applied = () =>
  typeof document !== 'undefined'
    ? valid(document.documentElement.getAttribute('data-theme'))
    : null;

const SunIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z" />
  </svg>
);

/**
 * The site follows prefers-color-scheme until someone chooses otherwise;
 * from then on the choice wins and persists. The tokens already carry the
 * two-way override (`:root[data-theme=...]`), so this only has to set the
 * attribute.
 */
const ThemeToggle = () => {
  const [theme, setTheme] = useState(() => stored() ?? applied() ?? (systemPrefersDark() ? 'dark' : 'light'));

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem(KEY, theme); } catch { /* private mode */ }
  }, [theme]);

  const next = theme === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      className="nav__theme"
      onClick={() => setTheme(next)}
      aria-label={`Switch to ${next} theme`}
      title={`Switch to ${next} theme`}
    >
      {theme === 'dark' ? <MoonIcon /> : <SunIcon />}
    </button>
  );
};

export default ThemeToggle;
